import { useState, useEffect, useMemo } from 'react';

import {
  Grid,
  DialogContentText,
  DialogProps,
  Checkbox,
  FormControlLabel,
  Box,
} from '@mui/material';
import useAxios from 'axios-hooks';
import { useForm } from 'react-hook-form';

import { Card } from 'components';
import { AppDialog } from 'components/Dialogs';
import { TextFormField, SelectFormField } from 'components/FormFields';
import strings, { url } from 'constants/index';
import { useEntityType, EntityTypes } from 'contexts/EntityTypesContext';
import { buildRequestParams } from 'utils/pagination.utils';

import { FixtureCompetitorCollision } from '../FixtureCompetitorCollision/FixtureCompetitorCollision';

export interface FixtureRecreateFormValues {
  isRecreating: boolean;
  formData?: { ReasonForRecreate?: string; NoteForRecreate?: string };
}

const defaultValues = {
  ReasonForRecreate: '',
  NoteForRecreate: '',
};

interface FixtureRecreateDialogProps extends DialogProps {
  message: string;
  onSubmit: (submitData: any) => void;
  curationPropertiesData: CurationProperties;
}

export const FixtureRecreateDialog = ({
  message,
  onSubmit,
  open,
  onClose,
  curationPropertiesData,
}: FixtureRecreateDialogProps) => {
  const [isRecreating, setIsRecreating] = useState(true);

  const fixtureType = useEntityType(EntityTypes.Fixtures);

  const [{ data: curationPropertiesTypes }] = useAxios<PaginationData<EntityCurationProperty>>(
    {
      url: url.curation_property_types,
      params: buildRequestParams({
        filters: [
          ...(fixtureType?.id
            ? [{ columnId: 'entityTypeId', value: fixtureType.id }]
            : []),
          { columnId: 'name', value: 'ReasonForRecreate' },
        ],
      }),
    },
    // Dialog is only mounted while open; always load recreate reasons.
    { manual: false },
  );

  const reasons = useMemo(
    () =>
      curationPropertiesTypes?.items[0]?.curationPropertyTypeAllowedValues.map(
        ({ allowedValue }) => ({
          label: allowedValue,
          value: allowedValue,
        }),
      ) || [],
    [curationPropertiesTypes?.items],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm({
    defaultValues,
    mode: 'onChange',
  });

  useEffect(() => () => reset(defaultValues), [reset]);

  const handleSave = () => {
    if (isRecreating) {
      handleSubmit((formData: any) =>
        onSubmit({
          formData: {
            curationProperties: { ...curationPropertiesData.curationProperties, ...formData },
          },
          isRecreating,
        }),
      )();
    } else {
      onSubmit({ isRecreating });
    }
  };

  return (
    <>
      <AppDialog
        open={open}
        title={strings.warning}
        fullWidth={false}
        onClose={onClose}
        confirmButton={{
          label: strings.save,
          props: {
            onClick: handleSave,
            disabled: isRecreating && (!isDirty || !isValid),
          },
        }}
      >
        <DialogContentText>{message}</DialogContentText>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6} md={6}>
            <Card
              sx={
                !isRecreating
                  ? { border: '1px solid !important', borderColor: 'primary.main !important' }
                  : {}
              }
              title={
                <FormControlLabel
                  value="isUpdating"
                  control={
                    <Checkbox checked={!isRecreating} onChange={() => setIsRecreating(false)} />
                  }
                  label={strings.fixture_update}
                />
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Card
              sx={
                isRecreating
                  ? { border: '1px solid !important', borderColor: 'primary.main !important' }
                  : {}
              }
              title={
                <FormControlLabel
                  value="isRecreating"
                  control={
                    <Checkbox checked={isRecreating} onChange={() => setIsRecreating(true)} />
                  }
                  label={strings.fixture_recreate}
                />
              }
            >
              <form noValidate>
                <Box sx={{ '& > .MuiFormControl-root': { mb: 2 } }}>
                  <SelectFormField
                    control={control}
                    disabled={!isRecreating}
                    label={strings.reason}
                    name="ReasonForRecreate"
                    values={reasons}
                    required
                  />
                  <TextFormField
                    control={control}
                    disabled={!isRecreating}
                    label={strings.note}
                    name="NoteForRecreate"
                    fullWidth
                    required
                  />
                </Box>
              </form>
            </Card>
          </Grid>
        </Grid>
      </AppDialog>
      <FixtureCompetitorCollision onClick={handleSave} />
    </>
  );
};
